import { FeatureLike } from "ol/Feature";
import { Fill, Icon, Stroke, Style, Text } from "ol/style";
import CircleStyle from "ol/style/Circle";

/*
 * Описание поддерживаемых конфигураций стиля для слоя.
 */
type IconCfg = {
  type: "icon";
  src: string;
  scale?: number;
  anchor?: [number, number]; // доли (0–1)
};

type TextBackgroundCfg = {
  color?: string; // цвет заливки прямоугольника
  stroke?: string; // цвет обводки
  padding?: [number, number, number, number]; // top,right,bottom,left (px)
};

type TextCfg = {
  field: string; // свойство Feature для подписи
  font?: string; // CSS-шрифт, например "12px Roboto"
  fill?: string; // цвет текста
  offsetY?: number; // смещение по Y (px)
  background?: TextBackgroundCfg;
};

type CircleCfg = {
  type: "circle";
  radius?: number;
  fill?: string;
  stroke?: { color?: string; width?: number };
  text?: TextCfg;
};

type FaIconCfg = {
  type: "fa-icon";
  glyph: string; // пример: "\uf3c5" или "fa-solid fa-location-dot"
  size?: number; // px
  color?: string;
};

type StyleCfg = IconCfg | CircleCfg | FaIconCfg;

/**
 * Создаёт ol/style.Style на основании декларативного объекта в конфиге.
 * Возвращает функцию-генератор, чтобы подписям можно было динамически
 * подтягивать свойства конкретного Feature.
 */
export function buildStyle(cfg: StyleCfg): (feature: FeatureLike) => Style {
  switch (cfg.type) {
    /* ---------- PNG / SVG иконка ---------- */
    case "icon":
      return () =>
        new Style({
          image: new Icon({
            src: cfg.src,
            scale: cfg.scale ?? 1,
            anchor: cfg.anchor ?? [0.5, 1],
            anchorXUnits: "fraction",
            anchorYUnits: "fraction",
          }),
        });

    /* ---------- Круг + необязательная подпись ---------- */
    case "circle":
      return (feature) => {
        const txtCfg = cfg.text;
        return new Style({
          image: new CircleStyle({
            radius: cfg.radius ?? 6,
            fill: new Fill({ color: cfg.fill ?? "#3388ff" }),
            stroke: new Stroke({
              color: cfg.stroke?.color ?? "#ffffff",
              width: cfg.stroke?.width ?? 1,
            }),
          }),
          text:
            txtCfg !== undefined
              ? new Text({
                  text: String(feature.get(txtCfg.field) ?? ""),
                  font: txtCfg.font ?? "12px Arial,sans-serif",
                  fill: new Fill({ color: txtCfg.fill ?? "#ffffff" }),
                  offsetY: txtCfg.offsetY ?? -12,
                  overflow: true,
                  backgroundFill: txtCfg.background?.color
                    ? new Fill({ color: txtCfg.background.color })
                    : undefined,
                  backgroundStroke: txtCfg.background?.stroke
                    ? new Stroke({ color: txtCfg.background.stroke, width: 1 })
                    : undefined,
                  padding: txtCfg.background?.padding ?? [2, 4, 2, 4],
                })
              : undefined,
        });
      };

    /* ---------- FontAwesome глиф (рендерим на canvas) ---------- */
    case "fa-icon":
      return () => {
        const size = cfg.size ?? 18;
        const canvas = document.createElement("canvas");
        canvas.width = canvas.height = size * 2;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.font = `${size}px \"Font Awesome 6 Free\"`;
          ctx.fillStyle = cfg.color ?? "#ff0000";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";

          // Если в glyph передан unicode-код, выводим его, иначе можно
          // дополнить маппинг классов FontAwesome → unicode.
          const glyph = cfg.glyph.startsWith("\\u") || cfg.glyph.length === 1 ? cfg.glyph : "\uf3c5";
          ctx.fillText(glyph, size, size);
        }

        return new Style({
          image: new Icon({
            img: canvas,
            imgSize: [canvas.width, canvas.height],
            anchor: [0.5, 0.5],
            anchorXUnits: "fraction",
            anchorYUnits: "fraction",
          }),
        });
      };

    default:
      return () => new Style();
  }
} 